import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// Last-10 chronological touch points for a single product code, from
// purchase order through to dispatch -- the product-level counterpart to
// OrderTraceModal. Backed by get_product_trace(), which unions PO Created /
// PO Received / Sold to Customer / Linked to Smart Buyer PO / Dispatched
// events, takes the most recent 10 by date, and hands them back newest
// first -- reversed here so the modal reads oldest-to-newest, like a story.
// Usage: <ProductTraceModal itemCode={code} onClose={() => setCode(null)} />

function fmtDateTime(val) {
  if (!val) return '—';
  return new Date(val).toLocaleString('en-NZ', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

const MUTED = '#9AA9BC';

const EVENT_ICON = {
  'PO Created':              '📝',
  'PO Received':             '📦',
  'Sold to Customer':        '🛒',
  'Linked to Smart Buyer PO': '🔗',
  'Dispatched':              '🚚',
};

const EVENT_COLOR = {
  'PO Created':              '#7DB3E8',
  'PO Received':             '#6FCF97',
  'Sold to Customer':        '#C8922A',
  'Linked to Smart Buyer PO': '#B78CD8',
  'Dispatched':              '#6FCF97',
};

export default function ProductTraceModal({ itemCode, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data: result, error: rpcError } = await supabase
      .rpc('get_product_trace', { p_item_code: itemCode });
    if (rpcError) {
      setError(rpcError.message);
      setData(null);
    } else if (!result) {
      setError(`No product found matching "${itemCode}"`);
      setData(null);
    } else {
      setData(result);
    }
    setLoading(false);
  }, [itemCode]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Oldest first -- PO through to dispatch, read top to bottom.
  const events = data?.events ? [...data.events].reverse() : [];

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(13, 21, 32, 0.55)', backdropFilter: 'blur(2px)', zIndex: 1000 }}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 1001, width: 720, maxWidth: '95vw', maxHeight: '88vh',
        display: 'flex', flexDirection: 'column',
        background: 'var(--surface, #151F2E)', border: '1px solid var(--border, #1E2D42)',
        borderRadius: 10, boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        fontFamily: 'var(--font, Helvetica, sans-serif)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px 12px', borderBottom: '1px solid var(--border, #1E2D42)',
          background: 'var(--surface-raised, #0F1A28)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>📦</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text, #E8EDF4)' }}>
                Product Trace — {itemCode}
              </div>
              {data?.product_name && (
                <div style={{ fontSize: 11, color: MUTED, marginTop: 1 }}>
                  {data.product_name}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={load} disabled={loading} title="Refresh" style={{
              background: 'var(--surface, #151F2E)', border: '1px solid var(--border, #1E2D42)',
              borderRadius: 5, color: MUTED, cursor: loading ? 'default' : 'pointer',
              fontSize: 13, padding: '4px 10px', opacity: loading ? 0.5 : 1,
            }}>
              {loading ? '…' : '↻'}
            </button>
            <button onClick={onClose} title="Close (Esc)" style={{
              background: 'transparent', border: 'none', color: MUTED,
              cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 4px',
            }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
          {loading && (
            <div style={{ padding: 40, textAlign: 'center', color: MUTED, fontSize: 13 }}>Loading…</div>
          )}
          {!loading && error && (
            <div style={{ padding: 24, color: '#FF6B6B', fontSize: 13 }}>❌ {error}</div>
          )}
          {!loading && !error && data && (
            <>
              {!events.length && (
                <div style={{ padding: 24, textAlign: 'center', color: MUTED, fontSize: 13 }}>
                  No purchase order, sales order, or dispatch activity found for this item.
                </div>
              )}
              {events.length > 0 && (
                <div style={{ fontSize: 11, color: MUTED, marginBottom: 12, textTransform: 'uppercase', fontWeight: 700 }}>
                  Last {events.length} Touch Point{events.length === 1 ? '' : 's'} — Oldest to Newest
                </div>
              )}
              {events.map((ev, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 12, marginBottom: 10, paddingBottom: 10,
                  borderBottom: i < events.length - 1 ? '1px solid var(--border, #1E2D42)' : 'none',
                }}>
                  <div style={{ fontSize: 18, flexShrink: 0, width: 24, textAlign: 'center' }}>
                    {EVENT_ICON[ev.event_type] || '•'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: EVENT_COLOR[ev.event_type] || 'var(--text, #E8EDF4)' }}>
                        {ev.event_type}
                      </span>
                      <span style={{ fontSize: 11, color: MUTED }}>{fmtDateTime(ev.event_date)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text, #E8EDF4)', marginTop: 3 }}>
                      <span className="code-badge code-badge--sm" style={{ marginRight: 6 }}>{ev.order_ref || '—'}</span>
                      {ev.counterparty && <span>{ev.counterparty}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {ev.qty != null && <span>qty {ev.qty}</span>}
                      {ev.amount != null && <span>${Number(ev.amount).toFixed(2)}</span>}
                      {ev.status_or_stage && <span>{ev.status_or_stage}</span>}
                      {ev.detail && <span style={{ fontFamily: 'monospace' }}>{ev.detail}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '10px 20px', borderTop: '1px solid var(--border, #1E2D42)',
          background: 'var(--surface-raised, #0F1A28)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            background: 'var(--surface, #151F2E)', border: '1px solid var(--border, #1E2D42)',
            borderRadius: 5, color: 'var(--text, #E8EDF4)', cursor: 'pointer', fontSize: 12, padding: '5px 14px',
          }}>Close</button>
        </div>
      </div>
    </>
  );
}
